import React from "react";
import AccordionEditable from "../../../../../components/ui/accordions/AccordionEditable";
import ContentCard from "../../../../../components/ui/cards/ContentCard";
import { cryptocurrencyExchanges } from "../../../../../components/ui/forms/selectOptionData";
import DocumentList from "../../../../../components/ui/forms/DocumentList";
import Tooltip from "../../../../../components/ui/Tooltip";
import { SectionUploadButton, SectionMetaRow } from "./SectionSharedComponents";

// Helper to get tooltip content for source of income fields
const getSourceOfIncomeTooltip = (fieldName) => {
  const tooltipMap = {
    tenForm: "Upload 1099-NEC form only",
    stockOrDividends: "Upload broker statement only",
    scheduleK: "Upload Form 1065 K-1, Form 1120-S K-1, Form 1041 only",
    cryptoTransaction:
      "Upload 1099's or all transactions and generate Gain & Loss depending on exchange.",
    llc_owner:
      "Upload Business Documents: Profit & Loss statement OR all receipts/invoices consolidated in a single document",
  };

  return tooltipMap[fieldName] || null;
};

const PTFTaxSituationSection = ({
  situation,
  documents = [],
  isCpa,
  onOpenUploadModal,
  onDocumentDeleted,
  onMarkCryptoComplete,
  onEnableCryptoUpload,
  onOpenGenerateGainLossModal,
  summaryMetrics,
  disableDelete = false,
}) => {
  if (!situation) return null;

  // Get metrics from API
  const metrics = summaryMetrics?.income_sources_ptf || {};

  const taxYearsArray = Array.isArray(situation.taxYears)
    ? situation.taxYears
    : situation.taxYears
    ? [situation.taxYears]
    : [];
  const taxYearsNumeric = taxYearsArray.map((year) => Number(year));

  // Filter documents by document type and year
  const getSourceDocuments = (documentType) => {
    if (!documentType) return [];
    return documents.filter((doc) => {
      if (!doc.documentType) return false;
      const docTypeMatch =
        doc.documentType.toUpperCase() === documentType.toUpperCase();
      const docYear = doc.documentYear ? Number(doc.documentYear) : null;
      const yearMatch = docYear !== null && taxYearsNumeric.includes(docYear);
      return docTypeMatch && yearMatch;
    });
  };

  // Check if any income sources are active
  const hasAnySources =
    situation.wtwoForm?.value ||
    situation.tenForm?.value ||
    situation.miscForm?.value ||
    situation.stockOrDividends?.value ||
    situation.scheduleK?.value ||
    situation.cryptoTransaction?.value ||
    situation.llc_owner?.value ||
    situation.foreignIncome?.value ||
    situation.rentalProperties?.value ||
    situation.propertiesAssets?.value;

  // Generic document card using API metrics
  const renderDocumentCard = (
    key,
    title,
    metricKey,
    uploadConfig,
    tooltip = null,
  ) => {
    const docMetrics = metrics[metricKey];
    if (!docMetrics) return null;

    const { required, provided, complete } = docMetrics;
    const needsAttention = !complete;
    const sourceDocuments = getSourceDocuments(metricKey.toUpperCase());

    return (
      <ContentCard key={key}>
        <div className="section-header-row">
          {needsAttention && (
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
              alt="Attention needed"
              className="attention-indicator-icon"
            />
          )}
          <h4 className="section-header-title">{title}</h4>
          {tooltip && <Tooltip icon="info" content={tooltip} />}
          <SectionUploadButton
            show={!isCpa && needsAttention}
            onClick={() => onOpenUploadModal(uploadConfig)}
          />
        </div>
        <SectionMetaRow required={required} provided={provided} />
        {sourceDocuments.length > 0 && (
          <DocumentList
            documents={sourceDocuments}
            onDocumentDeleted={onDocumentDeleted}
            isCpa={isCpa}
            disableDelete={disableDelete}
          />
        )}
      </ContentCard>
    );
  };

  // Render W2 Card
  const renderW2Card = () => {
    if (!situation.wtwoForm?.value) return null;
    return renderDocumentCard("w2", "W2", "w2", {
      name: "wtwoForm",
      label: "W2",
    });
  };

  // Render Self-Employed Card (1099 - backend combines tenForm + miscForm)
  const renderSelfEmployedCard = () => {
    const hasTenForm = situation.tenForm?.value === true;
    const hasMiscForm = situation.miscForm?.value === true;
    if (!hasTenForm && !hasMiscForm) return null;

    // Backend now combines tenForm + miscForm into single "1099" key
    const combinedMetrics = metrics["1099"] || {
      required: 0,
      provided: 0,
      complete: true,
    };

    const required = combinedMetrics.required;
    const provided = combinedMetrics.provided;
    const needsAttention = !combinedMetrics.complete;

    const sourceDocuments = [
      ...getSourceDocuments("1099"),
      ...getSourceDocuments("1099_MISC"),
    ];
    const tooltip = getSourceOfIncomeTooltip("tenForm");

    return (
      <ContentCard key="self-employed">
        <div className="section-header-row">
          {needsAttention && (
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
              alt="Attention needed"
              className="attention-indicator-icon"
            />
          )}
          <h4 className="section-header-title">Self-Employed</h4>
          {tooltip && <Tooltip icon="info" content={tooltip} />}
          <SectionUploadButton
            show={!isCpa && needsAttention}
            onClick={() =>
              onOpenUploadModal({
                name: "tenForm",
                label: "Self-Employed (1099)",
              })
            }
          />
        </div>
        <SectionMetaRow required={required} provided={provided} />
        {sourceDocuments.length > 0 && (
          <DocumentList
            documents={sourceDocuments}
            onDocumentDeleted={onDocumentDeleted}
            isCpa={isCpa}
            disableDelete={disableDelete}
          />
        )}
      </ContentCard>
    );
  };

  // Render Investment Card
  const renderInvestmentCard = () => {
    if (!situation.stockOrDividends?.value) return null;
    return renderDocumentCard(
      "investment",
      "Investment/Brokerage",
      "1099_brokerage",
      { name: "stockOrDividends", label: "Investment/Brokerage" },
      getSourceOfIncomeTooltip("stockOrDividends"),
    );
  };

  // Render Schedule K Card
  const renderScheduleKCard = () => {
    if (!situation.scheduleK?.value) return null;
    return renderDocumentCard(
      "schedule-k",
      "Schedule K",
      "schedule_k",
      { name: "scheduleK", label: "Schedule K" },
      getSourceOfIncomeTooltip("scheduleK"),
    );
  };

  // Render Crypto Card (special handling for exchanges)
  const renderCryptoCard = () => {
    if (!situation.cryptoTransaction?.value) return null;

    const cryptoMetrics = metrics["crypto"] || {
      required: 0,
      provided: 0,
      complete: true,
    };
    const exchanges = situation.cryptoTransaction.fields?.exchanges || [];
    const exchangeOptions = cryptocurrencyExchanges();
    const uploadCompleted =
      situation.cryptoTransaction.fields?.upload_completed === true;

    const getExchangeLabel = (exchangeValue) => {
      const exchange = exchangeOptions.find(
        (opt) => opt.value === exchangeValue,
      );
      return exchange ? exchange.label : exchangeValue;
    };

    const getExchangeConfig = (exchangeValue) => {
      return exchangeOptions.find((opt) => opt.value === exchangeValue);
    };

    const getDocumentsForExchange = (exchangeValue) => {
      // const exchangeLabel = getExchangeLabel(exchangeValue);
      const filtered_documents = documents.filter((doc) => {
        if (!doc.documentType) return false;
        // Accept both CRYPTO (transactions) and CRYPTO_1099 (1099 forms)
        const docType = doc.documentType.toUpperCase();
        const docTypeMatch =
          (docType === "CRYPTO" && exchangeValue === "kraken") ||
          (docType === "CRYPTO_1099" && exchangeValue === "coinbase");
        const docYear = doc.documentYear ? Number(doc.documentYear) : null;
        const yearMatch = docYear !== null && taxYearsNumeric.includes(docYear);
        // const docName = doc.documentName?.toLowerCase() || "";
        // const exchangeNameLower = exchangeLabel.toLowerCase();
        // const exchangeValueLower = exchangeValue.toLowerCase();
        // const hasExchangeInName =
        //   docName.includes(exchangeNameLower) ||
        //   docName.includes(exchangeValueLower) ||
        //   (doc.exchange && doc.exchange.toLowerCase() === exchangeValueLower);
        // console.log(docTypeMatch, yearMatch, hasExchangeInName);
        return docTypeMatch && yearMatch; // && hasExchangeInName;
      });
      return filtered_documents;
    };

    const gainLossReports = documents.filter((doc) => {
      if (!doc.documentType) return false;
      const docTypeMatch =
        doc.documentType.toUpperCase() === "CRYPTO_GAIN_LOSS_REPORT";
      const docYear = doc.documentYear ? Number(doc.documentYear) : null;
      const yearMatch = docYear !== null && taxYearsNumeric.includes(docYear);
      return docTypeMatch && yearMatch;
    });

    const needsAttention = !cryptoMetrics.complete;
    const tooltip = getSourceOfIncomeTooltip("cryptoTransaction");

    return (
      <ContentCard key="crypto">
        <div className="section-header-row">
          {needsAttention && (
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
              alt="Attention needed"
              className="attention-indicator-icon"
            />
          )}
          <h4 className="section-header-title">Crypto</h4>
          {tooltip && <Tooltip icon="info" content={tooltip} />}
        </div>

        {/* Render each exchange */}
        {exchanges.length > 0 && (
          <div className="crypto-exchanges-list">
            {exchanges.map((exchangeValue) => {
              const exchangeLabel = getExchangeLabel(exchangeValue);
              const exchangeConfig = getExchangeConfig(exchangeValue);
              const exchangeDocuments = getDocumentsForExchange(exchangeValue);

              // Get metrics from backend for this specific exchange
              const exchangeMetricsKey = `cryptoTransaction_${exchangeValue}`;
              const exchangeMetrics = metrics[exchangeMetricsKey] || {
                required: 1,
                provided: 0,
                complete: false,
              };
              const isTransactionExchange =
                exchangeConfig?.documentType === "transactions";
              const required = exchangeMetrics.required;
              const submitted = exchangeMetrics.provided;
              const isComplete = exchangeMetrics.complete;

              // Determine if upload button should be shown
              // For transaction exchanges (Kraken): hide when uploadCompleted
              // For 1099 exchanges (Coinbase): hide when isComplete
              const shouldShowUpload = isTransactionExchange
                ? !uploadCompleted
                : !isComplete;

              return (
                <div key={exchangeValue} className="crypto-exchange-card">
                  <div className="crypto-exchange-header">
                    <span className="crypto-exchange-name">
                      {exchangeLabel}
                    </span>
                    <SectionUploadButton
                      show={!isCpa && shouldShowUpload}
                      onClick={() =>
                        onOpenUploadModal({
                          name: "cryptoTransaction",
                          label: exchangeLabel,
                          exchange: exchangeValue,
                          documentType: exchangeConfig?.documentType,
                          expectedCount: !isTransactionExchange
                            ? required
                            : null,
                        })
                      }
                    />
                    {/* Only show "Uploads disabled" for Kraken when manually marked complete */}
                    {isTransactionExchange && uploadCompleted && (
                      <span className="crypto-upload-disabled-text">
                        Uploads disabled
                      </span>
                    )}
                  </div>
                  {/* Only show Required/Submitted for 1099 exchanges (Coinbase) */}
                  {!isTransactionExchange && (
                    <SectionMetaRow required={required} provided={submitted} />
                  )}
                  {exchangeDocuments.length > 0 && (
                    <DocumentList
                      documents={exchangeDocuments}
                      onDocumentDeleted={onDocumentDeleted}
                      isCpa={isCpa}
                      disableDelete={
                        isTransactionExchange
                          ? uploadCompleted || disableDelete
                          : disableDelete
                      }
                    />
                  )}

                  {/* Gain & Loss Report */}
                  {exchangeValue === "kraken" && gainLossReports.length > 0 && (
                    <div
                      style={{
                        marginTop: "12px",
                        paddingTop: "12px",
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      <span
                        className="crypto-exchange-name"
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          marginBottom: "8px",
                          display: "block",
                        }}
                      >
                        Gain & Loss Report
                      </span>
                      <DocumentList
                        documents={gainLossReports}
                        onDocumentDeleted={onDocumentDeleted}
                        isCpa={isCpa}
                        disableDelete={uploadCompleted || disableDelete}
                      />
                    </div>
                  )}

                  {/* Actions - Only show when there are documents */}
                  {!isCpa &&
                    exchangeValue === "kraken" &&
                    exchangeDocuments.length > 0 && (
                      <div
                        className="crypto-actions-row"
                        style={{ marginTop: "12px" }}
                      >
                        {uploadCompleted ? (
                          <>
                            {gainLossReports.length === 0 && (
                              <button
                                type="button"
                                className="cta-button primary"
                                style={{ width: "auto" }}
                                onClick={() =>
                                  onOpenGenerateGainLossModal &&
                                  onOpenGenerateGainLossModal()
                                }
                              >
                                Generate Gain & Loss
                              </button>
                            )}
                            <button
                              type="button"
                              className="cta-button secondary"
                              style={{ width: "auto" }}
                              onClick={() =>
                                onEnableCryptoUpload && onEnableCryptoUpload()
                              }
                            >
                              Enable Upload
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="cta-button secondary"
                            style={{ width: "auto" }}
                            onClick={() =>
                              onMarkCryptoComplete && onMarkCryptoComplete()
                            }
                          >
                            Mark as Complete
                          </button>
                        )}
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        )}
      </ContentCard>
    );
  };

  // Render Business/LLC Card
  const renderBusinessLLCCard = () => {
    if (!situation.llc_owner?.value) return null;
    return renderDocumentCard(
      "business-llc",
      "Business/LLC",
      "llc",
      { name: "llc_owner", label: "Business/LLC" },
      getSourceOfIncomeTooltip("llc_owner"),
    );
  };

  // Render Foreign Income Card
  const renderForeignIncomeCard = () => {
    if (!situation.foreignIncome?.value) return null;
    return renderDocumentCard(
      "foreign-income",
      "Foreign Income",
      "foreign_income_statement",
      { name: "foreignIncome", label: "Foreign Income" },
    );
  };

  // Render Rental Properties Card
  const renderRentalPropertiesCard = () => {
    if (!situation.rentalProperties?.value) return null;

    const properties = situation.rentalProperties.fields?.properties || [];
    const totalCount =
      situation.rentalProperties.fields?.rentalPropertiesCount ||
      properties.length ||
      0;

    // Get metrics for rental documents
    const rentalIncomeMetrics = metrics["rental_income"] || {
      required: 0,
      provided: 0,
      complete: true,
    };
    const rentalExpensesMetrics = metrics["rental_expenses"] || {
      required: 0,
      provided: 0,
      complete: true,
    };
    const rentalDepreciationMetrics = metrics["rental_depreciation"] || {
      required: 0,
      provided: 0,
      complete: true,
    };

    const needsAttention =
      !rentalIncomeMetrics.complete || !rentalExpensesMetrics.complete;

    return (
      <ContentCard key="rental-properties">
        <div className="section-header-row">
          {needsAttention && (
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
              alt="Attention needed"
              className="attention-indicator-icon"
            />
          )}
          <h4 className="section-header-title">Rental Properties</h4>
        </div>
        <div className="section-total-count">
          Total Properties: {totalCount}
        </div>

        {/* Property Details */}
        {properties.length > 0 && (
          <div className="crypto-exchanges-list">
            {properties.map((property, index) => {
              const propertyNumber = index + 1;
              const propertyType = property.isResidential
                ? "Residential"
                : "Commercial";
              const rentType = property.isShortTermRental
                ? "Short-term"
                : "Long-Term";
              const newlyPurchased = property.isNewlyPurchased ? "Yes" : "No";

              const belongsToProperty = (doc, docTypePattern) => {
                const docType = doc.documentType?.toUpperCase();
                if (docType !== docTypePattern) return false;
                if (doc.propertyIndex != null) {
                  return doc.propertyIndex === index;
                }
                return index === 0;
              };

              const incomeDocuments = documents.filter((doc) =>
                belongsToProperty(doc, "RENTAL_INCOME"),
              );
              const expensesDocuments = documents.filter((doc) =>
                belongsToProperty(doc, "RENTAL_EXPENSES"),
              );
              const depreciationDocuments = documents.filter((doc) =>
                belongsToProperty(doc, "RENTAL_DEPRECIATION"),
              );

              const hasIncomeDoc = incomeDocuments.length > 0;
              const hasExpensesDoc = expensesDocuments.length > 0;
              const hasDepreciationDoc = depreciationDocuments.length > 0;

              return (
                <div key={index} className="crypto-exchange-card">
                  <div>
                    <span className="crypto-exchange-name">
                      Property {index + 1}
                    </span>
                    <div className="property-details">
                      <span className="tax-situation-meta-text">
                        <strong>Type:</strong> {propertyType}
                      </span>
                      <span className="tax-situation-meta-text">
                        <strong>Rent:</strong> {rentType}
                      </span>
                      <span className="tax-situation-meta-text">
                        <strong>Newly Purchased:</strong> {newlyPurchased}
                      </span>
                    </div>
                  </div>

                  {/* Document Upload Sections */}
                  <div className="property-details-sections">
                    {/* Income */}
                    <div className="nested-doc-section">
                      <div className="nested-doc-section-header">
                        <span className="nested-doc-section-title">Income</span>
                        <SectionUploadButton
                          show={!hasIncomeDoc && !isCpa}
                          onClick={() =>
                            onOpenUploadModal({
                              name: "rentalProperties",
                              label: `Rental Property ${index + 1} - Income`,
                              propertyIndex: index,
                              documentType: "RENTAL_INCOME",
                            })
                          }
                        />
                      </div>
                      {hasIncomeDoc && (
                        <DocumentList
                          documents={incomeDocuments}
                          onDocumentDeleted={onDocumentDeleted}
                          isCpa={isCpa}
                          disableDelete={disableDelete}
                        />
                      )}
                    </div>

                    {/* Expenses */}
                    <div className="nested-doc-section">
                      <div className="nested-doc-section-header">
                        <span className="nested-doc-section-title">
                          Expenses
                        </span>
                        <SectionUploadButton
                          show={!hasExpensesDoc && !isCpa}
                          onClick={() =>
                            onOpenUploadModal({
                              name: "rentalProperties",
                              label: `Rental Property ${index + 1} - Expenses`,
                              propertyIndex: index,
                              documentType: "RENTAL_EXPENSES",
                            })
                          }
                        />
                      </div>
                      {hasExpensesDoc && (
                        <DocumentList
                          documents={expensesDocuments}
                          onDocumentDeleted={onDocumentDeleted}
                          isCpa={isCpa}
                          disableDelete={disableDelete}
                        />
                      )}
                    </div>

                    {/* Depreciation (Optional) */}
                    <div className="nested-doc-section">
                      <div className="nested-doc-section-header">
                        <span className="nested-doc-section-title">
                          Depreciation{" "}
                          <span className="nested-doc-section-optional">
                            (Optional)
                          </span>
                        </span>
                        <SectionUploadButton
                          show={!hasDepreciationDoc && !isCpa}
                          onClick={() =>
                            onOpenUploadModal({
                              name: "rentalProperties",
                              label: `Rental Property ${
                                index + 1
                              } - Depreciation`,
                              propertyIndex: index,
                              documentType: "RENTAL_DEPRECIATION",
                            })
                          }
                        />
                      </div>
                      {hasDepreciationDoc && (
                        <DocumentList
                          documents={depreciationDocuments}
                          onDocumentDeleted={onDocumentDeleted}
                          isCpa={isCpa}
                          disableDelete={disableDelete}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ContentCard>
    );
  };

  // Render Real Estate Sold Card
  const renderRealEstateSoldCard = () => {
    if (!situation.propertiesAssets?.value) return null;
    const realEstateMetrics = metrics["real_estate_sold"];
    if (!realEstateMetrics || realEstateMetrics.required === 0) return null;

    return renderDocumentCard(
      "real-estate-sold",
      "Real Estate Properties Sold",
      "real_estate_sold",
      {
        name: "propertiesAssets",
        label: "Real Estate Properties Sold",
        documentType: "REAL_ESTATE_SOLD",
      },
    );
  };

  // Render Other Assets Sold Card
  const renderOtherAssetsSoldCard = () => {
    if (!situation.propertiesAssets?.value) return null;
    const otherAssetsMetrics = metrics["other_assets_sold"];
    if (!otherAssetsMetrics || otherAssetsMetrics.required === 0) return null;

    return renderDocumentCard(
      "other-assets-sold",
      "Other Assets Sold",
      "other_assets_sold",
      {
        name: "propertiesAssets",
        label: "Other Assets Sold",
        documentType: "OTHER_ASSETS_SOLD",
      },
    );
  };

  // Use API metrics for accordion badges
  const sectionComplete = metrics.complete ?? false;
  const sectionNeedsAttention = metrics.attention_needed_cus ?? false;

  // CPA is "Waiting" when section not complete and CUS has pending actions
  const isWaitingForCus = isCpa && !sectionComplete && sectionNeedsAttention;

  return (
    <AccordionEditable
      key="tax-situation"
      title="Personal Income Sources"
      hasRequiredActions={!isCpa && sectionNeedsAttention}
      allActionsComplete={sectionComplete}
      isWaiting={isWaitingForCus}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {hasAnySources ? (
          <>
            {renderW2Card()}
            {renderSelfEmployedCard()}
            {renderInvestmentCard()}
            {renderScheduleKCard()}
            {renderCryptoCard()}
            {renderBusinessLLCCard()}
            {renderForeignIncomeCard()}
            {renderRentalPropertiesCard()}
            {renderRealEstateSoldCard()}
            {renderOtherAssetsSoldCard()}
          </>
        ) : (
          <div style={{ color: "#666", fontStyle: "italic" }}>
            No income sources specified
          </div>
        )}
      </div>
    </AccordionEditable>
  );
};

export default PTFTaxSituationSection;
